/**
 * RapidAPI-style About document template for marketplace API detail.
 * Used as the About editor starter and as seeded demo content.
 *
 * Sections (recommended for providers):
 *   1. Overview + one-liner
 *   2. Pitch
 *   3. Highlights
 *   4. FAQ
 *   5. Capabilities (grouped)
 *   6. Pricing / credit notes
 *   7. Use cases
 *   8. Response contract
 *   9. Terms (short)
 */
export const ABOUT_DOC_DEMO = `# API Overview

**Enrich profile and company data in real time.**

Unlock high-signal enrichment with a single xapi gateway key. Pull people profiles, company records, jobs, and posts without running your own scrapers — so research, CRM fill, and lead gen stay fast and current.

Need a custom package or higher quota? [Contact us](mailto:support@example.com).

---

## Highlights

- **Real-time enrichment** — Responses reflect live upstream data, not a stale cache dump.
- **Simple billing** — Per-call credits on each endpoint (see tables below).
- **Gateway-ready** — Auth with \`xapi-key\`; same path shape as the OpenAPI / Endpoints tab.
- **Privacy-minded** — We do not resell your request logs. Upstream compliance (e.g. GDPR) depends on the data source you configure.

---

## FAQ

**How fresh is the data?**

> Enrichment runs at request time. There is no multi-day offline warehouse for the default Profile / Company paths.

**Do unused credits roll over?**

> No. xapi balances are prepaid USD credit, not monthly Rapid-style plan allotments. Spend remains until used or expired per your account terms.

**How do I change pricing or add endpoints?**

> Providers: edit the service in **Workspace → 管理 API**, then publish a new revision. Consumers: open **Try it** or pull the live OpenAPI from the Endpoints tab.

**What if a request fails?**

> Platform / gateway errors are marked with \`X-XAPI-Source: platform\`. Upstream failures return the provider status when possible — see **Response contract** below. Failed platform auth / balance checks are not charged as successful calls.

---

## What you can do

### Profile

- Scrape full profile (skills, experience, open-to-work, more)
- Search people
- Recent activity time
- Profile posts, comments, reactions
- Connection & follower counts
- Given / received recommendations
- Verification & key dates (joined, contact updated, photo updated)

### Company

- Company details & search
- Jobs and job counts
- Domain lookup
- Company posts & comments
- Employees count / “people also viewed”
- Insights *(premium, if enabled)*

### Jobs

- Job details
- Search jobs (v1 / v2)
- Hiring team

### Posts & articles

- Search posts / by hashtag
- Get post, reposts, profile-posted jobs
- User articles, article comments & reactions

---

## Credit usage by endpoint

> Numbers below are **example** per-call costs for this demo. Your live prices are whatever is configured on each endpoint in the **Endpoints** tab.

### Profile API

| Endpoint | Credits |
| --- | --- |
| Get Profile Data | 1 |
| Get Profile Data By URL | 1 |
| Search People | 1 |
| Search People by URL | 1 |
| Get Profile Recent Activity Time | 1 |
| Get Profile's Posts | 1 |
| Get Profile Post and Comments | 1 |
| Get Profile Post Comment | 1 |
| Get Profile's Comments | 1 |
| Get Profile Connection & Follower Count | 1 |
| Get Profile Data + Connection Count | 1 |
| Get Given / Received Recommendations | 1 |
| Get Profile Reactions | 1 |
| About The Profile | 1 |
| Get Profile Data, Connection Count and Posts | 2 |
| Profile Data & Recommendations | 2 |
| Get Similar Profiles | 1 |

### Company API

| Endpoint | Credits |
| --- | --- |
| Get Company Details | 1 |
| Search Companies | 1 |
| Get Company Jobs | 1 |
| Get Company By Domain | 1 |
| Get Company Insights **[PREMIUM]** | 5 |
| Get Company Employees Count | 1 |
| Get Company Jobs Count | 1 |
| Get Company Pages People Also Viewed | 1 |
| Get Company's Post | 1 |
| Get Company Post Comments | 1 |

### Job API

| Endpoint | Credits |
| --- | --- |
| Get Job Details | 1 |
| Search Jobs | 1 |
| Search Jobs V2 | 1 |
| Get Hiring Team | 1 |

### Post API

| Endpoint | Credits |
| --- | --- |
| Search Posts | 1 |
| Search Post by Hashtag | 1 |
| Get Post | 1 |
| Get Post Reposts | 1 |
| Get Profile's Posted Jobs | 1 |

### Article API

| Endpoint | Credits |
| --- | --- |
| Get User Articles | 1 |
| Get Article | 1 |
| Get Article Comments | 1 |
| Get Article Reactions | 1 |

---

## Use cases

- **Profile import** — Pull work history, skills, open-to-work, courses, certificates into your ATS/CRM.
- **Refresh stale records** — Nightly/weekly re-enrich talent or accounts so titles and status stay current.
- **Track candidates** — Watch position or open-to-work changes to surface active talent.
- **Company context** — Attach firmographics before outbound or onboarding.
- **Job → lead gen** — Search live openings and sync into your pipeline.

---

## Response contract

Always check success before reading \`data\`.

**Success**

\`\`\`json
{
  "success": true,
  "message": "",
  "data": { }
}
\`\`\`

**Failed request (not charged as a successful enrichment)**

\`\`\`json
{
  "success": false,
  "message": "The request failed. You will not be charged for this request",
  "data": null
}
\`\`\`

**Business / access error**

\`\`\`json
{
  "success": false,
  "message": "This profile can't be accessed",
  "data": null
}
\`\`\`

On the xapi gateway, also inspect response headers:

- \`X-XAPI-Source: provider | platform\` — who produced the error
- \`X-XAPI-Request-Id\` — support / log correlation

---

## Terms (summary)

By calling this API you agree to use returned data lawfully, respect upstream terms, and not resell raw PII in violation of applicable regulation. Full marketplace terms apply in addition to any provider-specific policy published here.

*This document is a **demo About template**. Replace endpoints, credits, and contact links with your real service details before publish.*
`;

/** Shorter starter for empty About editors (same skeleton, less copy). */
export const ABOUT_DOC_STARTER = `# API Overview

**One-line value prop goes here.**

Short pitch: who this API is for, what problem it solves, and why xapi is a good place to call it.

Need help? [Contact the provider](mailto:support@example.com).

---

## Highlights

- **Feature one** — brief benefit
- **Feature two** — brief benefit
- **Billing** — per-call / per-token summary (details on Endpoints tab)

---

## FAQ

**How do I authenticate?**

> Send header \`xapi-key: <your key>\` on every request to the gateway host.

**Where is the live price list?**

> See the **Endpoints** tab (and OpenAPI export). Keep long marketing tables in sync when you change prices.

---

## Capabilities

### Group A

- Endpoint / workflow one
- Endpoint / workflow two

### Group B

- Endpoint / workflow three

---

## Use cases

- **Use case 1** — one sentence
- **Use case 2** — one sentence

---

## Response contract

\`\`\`json
{
  "success": true,
  "message": "",
  "data": {}
}
\`\`\`

\`\`\`json
{
  "success": false,
  "message": "error reason",
  "data": null
}
\`\`\`

---

## Terms (summary)

Lawful use only. Replace this section with your real terms if needed.
`;

export default ABOUT_DOC_DEMO;

/**
 * Sandbox 服务的 About 文档 —— 按探测到的 Adapter 能力生成，而不是给一份空骨架。
 *
 * Sandbox 的接口面由 xAPI Sandbox v1 协议固定（消费者对任意服务商用同一份代码），
 * 所以文档里"怎么调"这部分完全可以自动写出来，服务商只需要补自己的定位和支持方式。
 * 手写反而容易和真实接口面不一致 —— 能力是按 manifest 收窄的，写死一份必然漂移。
 */
export const buildSandboxAboutDoc = ({ name, provider, capabilities = {}, skus = [] } = {}) => {
  const cap = (key) => capabilities?.[key] === true;
  const suspendable = capabilities?.suspension?.supported === true
    || cap('pause') || cap('stop') || cap('hibernate');
  const rows = [
    ['创建 / 查询 / 销毁', true],
    ['执行命令', cap('exec')],
    ['文件读写', cap('files')],
    ['端口访问', cap('ports')],
    ['挂起 / 恢复', suspendable],
    ['快照', cap('snapshot')],
    ['GPU', cap('gpu')],
  ].filter(([, on]) => on).map(([label]) => `- ${label}`).join('\n');

  const skuRows = (skus || []).length
    ? (skus || []).map((sku) => {
        const r = sku.resources || {};
        const parts = [
          r.cpu ? `${r.cpu} vCPU` : null,
          r.memoryGiB ? `${r.memoryGiB} GiB` : null,
          r.volumeGiB ? `${r.volumeGiB} GiB 卷` : null,
        ].filter(Boolean).join(' · ');
        return `| ${sku.name || sku.id} | ${parts || '见规格配置'} |`;
      }).join('\n')
    : '| 见「管理 API」中的规格配置 | — |';

  return `# ${name || 'Sandbox'}

按秒计费的云端沙箱，通过 xAPI Sandbox v1 协议接入${provider ? `（Adapter：\`${provider}\`）` : ''}。

**同一份代码可以调用 xAPI 上任意服务商的沙箱** —— 接口面由协议固定，换服务商不用改代码。

---

## 能力

${rows}

---

## 可售规格

| 规格 | 资源 |
|---|---|
${skuRows}

实时价格以市场详情页与 \`GET /v1/offerings\` 为准。

---

## 怎么调用

两个入口，共用同一套预留与结算：

\`\`\`bash
# 指名用这一家
curl -X POST https://<host>.p.xapi.to/v1/sandboxes \\
  -H 'xapi-key: <your key>' -H 'content-type: application/json' \\
  -d '{"offeringId":"<从 /v1/offerings 获取>"}'

# 或者跨服务商自动选型（按价格与能力挑一家）
curl -X POST https://sandbox.xapi.to/v1/sandboxes \\
  -H 'xapi-key: <your key>' -H 'content-type: application/json' \\
  -d '{"requirements":{"cpu":{"min":2},"capabilities":["exec"]}}'
\`\`\`

用完记得销毁，否则会一直按运行状态计费：

\`\`\`bash
curl -X POST https://sandbox.xapi.to/v1/sandboxes/<id>/terminate -H 'xapi-key: <your key>'
\`\`\`

---

## 计费方式

按**状态时长**计费，精确到秒：运行中与挂起分别定价${suspendable ? '（挂起通常显著便宜）' : ''}。
持久卷、GPU 等资源按规格自动计入。平台滚动预留未来 5 分钟费用，余额不足会自动关停并结算。

---

## 关于我们

<!-- 补充：你是谁、机器跑在哪些区域、SLA、支持渠道 -->

有问题联系：<!-- support@example.com -->
`;
};
